import { create } from 'zustand';
import { doc, setDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import type { QueryClient } from '@tanstack/react-query';
import posthogJs from 'posthog-js';

import db from '@services/firebase-service';
import { getAuth, setAuth } from '@services/identity.service';
import { logsToAwsCloudWatch } from '@services/logger.service';
import { getZoomChallengeCode } from '@services/zoom';
import { getGuestLoginToken } from '@services/email-auth.service';
import { isMeetingContext } from '@zoom-app/helpers';
import { invokeZoomAppsSdk } from '@zoom-app/apis';

import { useTeamsStore } from '@store/teamsStore';

import type {
  runningContextStringType,
  runningContextType,
  meetingType,
  messageActionType,
  messageSenderType,
  connectPayloadType,
} from '@lib/zoom/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

declare const zoomSdk: AnyRecord;

type SetOrUpdate<T> = T | ((prev: T) => T);

export interface ZoomState {
  // --- raw state ---
  connected: boolean;
  appContext: AnyRecord | string | undefined;
  runningContext: runningContextStringType | undefined;
  userContext: AnyRecord | undefined;
  headerContext: runningContextType | undefined;
  screenShared: boolean;
  expandState: { action: string };
  meetingContext: meetingType | undefined;
  metaData: AnyRecord | undefined;
  uid: AnyRecord | undefined;
  clientInfo: AnyRecord;
  configResponse: AnyRecord | undefined;
  displayAuthPopup: boolean;
  sdkInitialized: boolean;
  pendingConnectPayload: connectPayloadType | null;
  hashedUid: string | null;
  hashedMid: string | null;
  challengeCode: string | undefined;
  verifierCode: string | undefined;

  // Pushed in by ZoomEffects from React Query — kept as `any` to match the
  // legacy Context typing; consumers index nested fields without narrowing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zoomPlatform: any;
  meetingInfo: AnyRecord;
  isPending: boolean;
  apiResourceFetched: boolean;

  // Effect coordination
  counter: number;
  authorizationStatus: boolean;

  // Injected by ZoomEffects on mount (needed for query cache writes)
  queryClient: QueryClient | null;

  // --- computed (selector helper) ---
  appReady: () => boolean;

  // --- setters ---
  setConnected: (v: boolean) => void;
  setAppContext: (v: ZoomState['appContext']) => void;
  setRunningContext: (v: ZoomState['runningContext']) => void;
  setUserContext: (v: ZoomState['userContext']) => void;
  setHeaderContext: (v: SetOrUpdate<ZoomState['headerContext']>) => void;
  setScreenShared: (v: boolean) => void;
  setExpandState: (v: { action: string }) => void;
  setMeetingContext: (v: meetingType | undefined) => void;
  setMetaData: (v: AnyRecord | undefined) => void;
  setUid: (v: AnyRecord | undefined) => void;
  setClientInfo: (v: SetOrUpdate<AnyRecord>) => void;
  setConfigResponse: (v: AnyRecord | undefined) => void;
  setDisplayAuthPopup: (v: boolean) => void;
  setSdkInitialized: (v: boolean) => void;
  setPendingConnectPayload: (v: connectPayloadType | null) => void;
  setHashedUid: (v: string | null) => void;
  setHashedMid: (v: string | null) => void;
  setChallengeCode: (code: string | undefined) => void;
  setVerifierCode: (code: string | undefined) => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setZoomPlatform: (v: any) => void;
  setMeetingInfo: (v: AnyRecord) => void;
  setIsPending: (v: boolean) => void;
  setApiResourceFetched: (v: boolean) => void;

  setCounter: (v: SetOrUpdate<number>) => void;
  setAuthorizationStatus: (v: boolean) => void;

  setQueryClient: (qc: QueryClient | null) => void;

  // --- pure helpers ---
  isLoggedIn: () => boolean;
  isGuestUser: () => boolean;
  getGreetings: () => string;
  getSender: () => 'meeting' | 'client';
  getMeetingKey: () => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logToCloudWatch: (logData: any, logMessage?: string, level?: string) => void;

  // --- SDK actions ---
  sendMessage: (action: messageActionType, payload: AnyRecord) => Promise<void>;
  openUrl: (url: string) => Promise<void>;
  invokeZoomAppsSdk: typeof invokeZoomAppsSdk;
  authorize: () => Promise<void>;
  initGuest: () => Promise<void>;
  _logOutZoomApp: () => Promise<void>;
  logOutZoomApp: () => Promise<void>;
  syncZoomPlatformToOtherContext: () => void;
  invalidateZoomPlatform: () => void;

  // --- Firestore ---
  updateMetaDataFS: (data: AnyRecord) => void;
  writePendingConnect: (payload: connectPayloadType) => Promise<void>;
  setResetFS: () => Promise<void>;
  startDataSync: () => Promise<void>;
  stopDataSync: () => void;
}

// Module-level handle for the meeting metadata Firestore subscription so
// startDataSync / stopDataSync survive across action calls without forcing
// an unsub function into reactive state.
let _metadataUnsub: (() => void) | null = null;

// React-Query cache key shape mirrors ZoomEffects so .setQueryData/.invalidate work.
const platformQueryKey = (): unknown[] => [
  'zoomPlatform',
  useTeamsStore.getState().teamContext?.oid,
];

const applyUpdate = <T,>(prev: T, value: SetOrUpdate<T>): T =>
  typeof value === 'function' ? (value as (p: T) => T)(prev) : value;

export const useZoomStore = create<ZoomState>()((set, get) => ({
  // --- defaults ---
  connected: false,
  appContext: undefined,
  runningContext: undefined,
  userContext: undefined,
  headerContext: undefined,
  screenShared: false,
  expandState: { action: 'collapse' },
  meetingContext: undefined,
  metaData: undefined,
  uid: undefined,
  clientInfo: {},
  configResponse: undefined,
  displayAuthPopup: false,
  sdkInitialized: false,
  pendingConnectPayload: null,
  hashedUid: null,
  hashedMid: null,
  challengeCode: undefined,
  verifierCode: undefined,
  zoomPlatform: undefined,
  meetingInfo: {},
  isPending: false,
  apiResourceFetched: false,
  counter: 0,
  authorizationStatus: false,
  queryClient: null,

  // --- computed ---
  appReady: () => {
    const s = get();
    return !!(s.sdkInitialized && s.runningContext && s.headerContext?.uid && s.zoomPlatform && s.hashedUid);
  },

  // --- setters ---
  setConnected: (connected) => set({ connected }),
  setAppContext: (appContext) => set({ appContext }),
  setRunningContext: (runningContext) => set({ runningContext }),
  setUserContext: (userContext) => set({ userContext }),
  setHeaderContext: (value) =>
    set((s) => ({ headerContext: applyUpdate(s.headerContext, value) })),
  setScreenShared: (screenShared) => set({ screenShared }),
  setExpandState: (expandState) => set({ expandState }),
  setMeetingContext: (meetingContext) => set({ meetingContext }),
  setMetaData: (metaData) => set({ metaData }),
  setUid: (uid) => set({ uid }),
  setClientInfo: (value) =>
    set((s) => ({ clientInfo: applyUpdate(s.clientInfo, value) })),
  setConfigResponse: (configResponse) => set({ configResponse }),
  setDisplayAuthPopup: (displayAuthPopup) => set({ displayAuthPopup }),
  setSdkInitialized: (sdkInitialized) => set({ sdkInitialized }),
  setPendingConnectPayload: (pendingConnectPayload) => set({ pendingConnectPayload }),
  setHashedUid: (hashedUid) => set({ hashedUid }),
  setHashedMid: (hashedMid) => set({ hashedMid }),
  setChallengeCode: (challengeCode) => set({ challengeCode }),
  setVerifierCode: (verifierCode) => set({ verifierCode }),

  setZoomPlatform: (zoomPlatform) => set({ zoomPlatform }),
  setMeetingInfo: (meetingInfo) => set({ meetingInfo }),
  setIsPending: (isPending) => set({ isPending }),
  setApiResourceFetched: (apiResourceFetched) => set({ apiResourceFetched }),

  setCounter: (value) => set((s) => ({ counter: applyUpdate(s.counter, value) })),
  setAuthorizationStatus: (authorizationStatus) => set({ authorizationStatus }),

  setQueryClient: (queryClient) => set({ queryClient }),

  // --- pure helpers ---
  isLoggedIn: () => {
    const user = useTeamsStore.getState().user;
    return !!user && user.user_role !== 'guest';
  },

  isGuestUser: () => {
    const user = useTeamsStore.getState().user;
    return !!user && user.user_role === 'guest';
  },

  getGreetings: () => {
    const isLoggedIn = get().isLoggedIn();
    const user = useTeamsStore.getState().user;
    if (isLoggedIn) {
      let name = user?.email?.split('@')[0] || '';
      if (name) name = name[0].toUpperCase() + name.slice(1);
      return `Hi ${name} 👋🏽`;
    }
    return 'Hi Guest 👋🏽';
  },

  getSender: () => {
    const { runningContext } = get();
    return runningContext && isMeetingContext(runningContext) ? 'meeting' : 'client';
  },

  getMeetingKey: () => {
    const { hashedUid, hashedMid } = get();
    if (!hashedUid) throw new Error('Missing hashed UID');
    if (!hashedMid) throw new Error('Missing hashed meeting ID');
    return `${hashedUid}:${hashedMid}`;
  },

  logToCloudWatch: (logData, logMessage = 'ZoomApp Log', level = 'INFO') => {
    const { zoomPlatform, userContext, clientInfo } = get();
    const auth = getAuth();
    logsToAwsCloudWatch({
      logMessage,
      logData: {
        platformId: zoomPlatform?.id,
        userContext,
        clientInfo,
        auth,
        ...logData,
      },
      level,
    });
  },

  // --- SDK actions ---
  invokeZoomAppsSdk,

  sendMessage: async (action, payload) => {
    try {
      await zoomSdk.postMessage({
        action,
        payload,
        sender: get().getSender() as messageSenderType,
      });
    } catch (e) {
      console.error('Error sending message', e);
    }
  },

  openUrl: async (url) => {
    await invokeZoomAppsSdk({ name: 'openUrl', options: { url } });
  },

  authorize: async () => {
    if (get().authorizationStatus) return;
    set({ authorizationStatus: true });
    const { runningContext } = get();
    const queryParams = {
      state: JSON.stringify({
        rctx: runningContext && isMeetingContext(runningContext) ? 'meeting' : 'client',
      }),
    };
    const res = await getZoomChallengeCode(queryParams);
    set({
      challengeCode: res.entity.challengeCode,
      verifierCode: res.entity.verifier,
    });
    zoomSdk.authorize({
      state: res.entity.state,
      codeChallenge: res.entity.challengeCode,
    }).catch((err: unknown) => console.log(err));
  },

  initGuest: async () => {
    const data = await getGuestLoginToken({ email: process.env.NEXT_PUBLIC_ZM_GUEST_EMAIL });
    if (data.status) {
      setAuth(data.entity[0]);
      useTeamsStore.getState().initTeam();
    }
  },

  _logOutZoomApp: async () => {
    await useTeamsStore.getState().doLogout();
    get().initGuest();
    get().queryClient?.invalidateQueries({ queryKey: ['zoomPlatform'] });
  },

  logOutZoomApp: async () => {
    get().sendMessage('removeAuth', {});
    await get()._logOutZoomApp();
  },

  syncZoomPlatformToOtherContext: () => {
    const { connected, zoomPlatform, sendMessage } = get();
    if (connected && zoomPlatform) {
      sendMessage('syncZoomPlatform', zoomPlatform);
    }
  },

  invalidateZoomPlatform: () => {
    get().queryClient?.invalidateQueries({ queryKey: platformQueryKey() });
  },

  // --- Firestore ---
  setResetFS: async () => {
    try {
      const docRef = doc(db, 'zoomapp-meetings', get().getMeetingKey());
      await setDoc(docRef, { createdAt: new Date().toISOString() });
    } catch (error) {
      get().logToCloudWatch({ error }, 'Error setting metadata', 'ERROR');
    }
  },

  updateMetaDataFS: (data) => {
    try {
      const docRef = doc(db, 'zoomapp-meetings', get().getMeetingKey());
      updateDoc(docRef, data).catch((error) => {
        get().logToCloudWatch({ error }, 'Error updating metadata', 'ERROR');
      });
    } catch (error) {
      get().logToCloudWatch({ error }, 'Error updating metadata', 'ERROR');
    }
  },

  stopDataSync: () => {
    if (_metadataUnsub) {
      _metadataUnsub();
      _metadataUnsub = null;
    }
  },

  startDataSync: async () => {
    await get().setResetFS();
    if (_metadataUnsub) _metadataUnsub();

    const docRef = doc(db, 'zoomapp-meetings', get().getMeetingKey());
    _metadataUnsub = onSnapshot(
      docRef,
      (docu) => {
        if (docu.exists()) {
          get().setMetaData(docu.data());
        }
      },
      (error) => {
        get().logToCloudWatch({ error }, 'Error subscribing to metadata', 'ERROR');
        setTimeout(() => {
          get().startDataSync();
        }, 3000);
      },
    );

    get().updateMetaDataFS({
      clientIsAlive: true,
      clientLastHeartBeat: new Date().getTime(),
    });
  },

  writePendingConnect: async (payload) => {
    set({ pendingConnectPayload: payload });
    const { hashedUid } = get();
    if (!hashedUid) return;
    try {
      const docRef = doc(db, 'zoomapp-pending', hashedUid);
      await setDoc(docRef, {
        ...payload,
        createdAt: new Date().toISOString(),
      });
    } catch (error) {
      get().logToCloudWatch({ error }, 'Error writing pending connect to Firestore', 'ERROR');
    }
  },
}));

// Re-export posthog so callers that previously read it from context can keep
// the same reference. It's a singleton — safe to read at module scope.
export const zoomPosthog = posthogJs;
